import type { RxDocument } from 'rxdb';

import { Button } from '@flintmint/web-shared/components/button';
import { Icon } from '@flintmint/web-shared/components/icon';

import { ContextItem, type ContextItemProps, type ManagedSyncStatus } from '@/components/context-item';
import { SourceInput } from '@/components/source-input';
import { getSourceDocument, type SourceSearchItem } from '@/hooks/use-source-list-model';
import { useResyncUserSource } from '@/hooks/use-resync-user-source';
import { isMediaSourceType } from '@/utils/source-preview';

import type { UserSource, UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';

import styles from './source-row.module.scss';

export type LocalSourceRowItem = {
  kind: 'uploading';
  id: string;
  name: string;
  sourceType?: string | null;
  status: 'uploading' | 'error';
  progress?: number;
  error?: string | null;
};

export type SourceRowItem = SourceSearchItem | LocalSourceRowItem;

export const isLocalSourceRowItem = (item: SourceRowItem): item is LocalSourceRowItem => {
  return typeof item === 'object' && item !== null && 'kind' in item;
};

export const SourceRow = (props: SourceRowProps) => {
  if (!isLocalSourceRowItem(props.item)) {
    return <PersistedSourceRow {...props} item={props.item} />;
  }

  return <ContextItem {...buildLocalItemProps(props.item, props)} />;
};

const PersistedSourceRow = (props: SourceRowProps & { item: SourceSearchItem }) => {
  const sourceDoc = getSourceDocument(props.item);
  const { isResyncing, resync } = useResyncUserSource(sourceDoc as UserSource);
  const { contextItemProps, availabilityScope } = buildPersistedItemProps(props.item, props);

  contextItemProps.syncStatus = isResyncing ? 'processing' : (sourceDoc.syncStatus as ManagedSyncStatus);
  if (sourceDoc.syncStatus === 'error') {
    contextItemProps.errorAction = <ResyncButton onClick={resync} />;
  }

  // Note-scoped sources can't be detached (it would orphan them), only deleted.
  // Callers may wire onDetachFromNote unconditionally, so we strip it here.
  if (availabilityScope === 'note') {
    contextItemProps.onDetachFromNote = undefined;
  }

  return <ContextItem {...contextItemProps} />;
};

const buildLocalItemProps = (item: LocalSourceRowItem, props: SourceRowProps): ContextItemProps => {
  const { appearance, onRemoveLocalItem, getSharedContextItemProps } = props;

  return {
    id: item.id,
    name: item.name,
    sourceType: item.sourceType,
    source: undefined,
    appearance,
    onDelete: () => onRemoveLocalItem?.(item.id),
    ...(getSharedContextItemProps?.(item.id, item.name, item.sourceType, undefined, true, undefined) ?? {}),
    uploadStatus: item.status,
    uploadProgress: item.progress,
    error: item.error,
  };
};

type BuiltContextItemProps = {
  contextItemProps: ContextItemProps;
  availabilityScope?: UserSourceAvailabilityScope;
};

const buildPersistedItemProps = (item: SourceSearchItem, props: SourceRowProps): BuiltContextItemProps => {
  const {
    onDetach,
    appearance,
    toggleAttachment,
    onOpenAudioPreview,
    showSourceInputMeta,
    persistedExtraProps,
    noteUserSources = {},
    canToggleEnabled = false,
    highlightAttached = false,
    onUserSourceEnabledChange,
    getSharedContextItemProps,
    canToggleAttachment = false,
  } = props;

  const sourceDoc = getSourceDocument(item);
  const sourceId = sourceDoc.id;
  const sourceAttachment = noteUserSources[sourceId];
  const sourceAttached = !!sourceAttachment;
  const sourceEnabled = sourceAttachment?.enabled ?? false;
  const availabilityScope = sourceDoc.availabilityScope as UserSourceAvailabilityScope | undefined;

  const sharedProps = getSharedContextItemProps?.(
    sourceId,
    sourceDoc.name,
    sourceDoc.sourceType,
    sourceDoc.source,
    false,
    availabilityScope
  ) ?? {
    onDetachFromNote: sourceAttached && onDetach ? () => onDetach(sourceId) : undefined,
  };

  const extraProps = persistedExtraProps?.({ item, sourceDoc, sourceId, sourceAttached, sourceEnabled }) ?? {};

  return {
    availabilityScope,
    contextItemProps: {
      id: sourceId,
      name: sourceDoc.name,
      error: sourceDoc.error,
      appearance,
      sourceType: sourceDoc.sourceType,
      source: sourceDoc.source,
      enabled: sourceEnabled,
      attached: sourceAttached,
      onToggleEnabled: canToggleEnabled ? () => onUserSourceEnabledChange?.(sourceId, !sourceEnabled) : undefined,
      onToggleAttachment: canToggleAttachment ? () => toggleAttachment?.(sourceId, sourceAttached) : undefined,
      openAudioInAModal: () => onOpenAudioPreview?.(sourceId),
      parsedContent: (sourceDoc as UserSource).parsedContent ?? sourceDoc.content ?? '',
      updatedAt: sourceDoc.updatedAt as string,
      syncStatus: sourceDoc.syncStatus as ManagedSyncStatus,
      highlightAttached: highlightAttached && sourceAttached,
      ...sharedProps,
      ...extraProps,
      meta:
        showSourceInputMeta && !isMediaSourceType(sourceDoc.sourceType) ? (
          <SourceInput userSource={sourceDoc as unknown as RxDocument<UserSource>} />
        ) : undefined,
    },
  };
};

interface SourceRowProps {
  item: SourceRowItem;
  canToggleEnabled?: boolean;
  highlightAttached?: boolean;
  showSourceInputMeta?: boolean;
  canToggleAttachment?: boolean;
  onDetach?: (id: string) => void;
  appearance?: 'default' | 'compact';
  onRemoveLocalItem?: (id: string) => void;
  onOpenAudioPreview?: (id: string) => void;
  toggleAttachment?: (sourceId: string, attached: boolean) => void;
  noteUserSources?: Record<string, { index?: number; enabled?: boolean }>;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void;
  getSharedContextItemProps?: (
    id: string,
    name: string,
    sourceType?: string | null,
    source?: string | null,
    uploading?: boolean,
    availabilityScope?: UserSourceAvailabilityScope
  ) => Partial<ContextItemProps>;
  persistedExtraProps?: (args: {
    item: SourceSearchItem;
    sourceDoc: ReturnType<typeof getSourceDocument>;
    sourceId: string;
    sourceAttached: boolean;
    sourceEnabled: boolean;
  }) => Partial<ContextItemProps>;
}

const ResyncButton = ({ onClick }: { onClick: () => void }) => (
  <Button appearance="plain" itemsDirection="end" className={styles.resyncButton} icon={<Icon type="repeat" />} onClick={onClick}>
    Resync
  </Button>
);
